import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import useCustomLabelsFromProductFeatures from 'bundles/custom-labels/hooks/useCustomLabelsFromProductFeatures';
import { useCustomLabelsMapping } from 'bundles/custom-labels/hooks/useCustomLabelsMapping';
import type { CustomLabels, ReplaceCustomContent } from 'bundles/custom-labels/types/CustomLabels';
import type {
  CustomLabelsV2ByCourseBranchQuery,
  CustomLabelsV2ByCourseBranchQueryVariables,
} from 'bundles/naptimejs/resources/__generated__/CustomLabelsV2';

export type OutputProps = {
  loading: boolean;
  error?: Error;
  customLabels?: CustomLabels;
  replaceCustomContent: ReplaceCustomContent;
  useModuleNameInsteadOfWeek?: boolean | null;
};

/* eslint-disable graphql/template-strings */
export const CustomLabelsQuery = gql`
  query CustomLabelsByBranchQuery($id: String!) {
    CustomLabelsV2 @naptime {
      byCourseBranch(id: $id) {
        elements {
          id
          moduleNames
          namings {
            courseraCourse
            courseraWeek
          }
          useModuleNameInsteadOfWeek
        }
      }
    }
  }
`;
/* eslint-enable graphql/template-strings */

const useCustomLabelsByCourseBranch = (courseBranchId?: string): OutputProps => {
  const { loading: loadingProductFeatures, enableCustomLabels } = useCustomLabelsFromProductFeatures(courseBranchId);

  const variables = courseBranchId ? { id: courseBranchId } : undefined;
  const { loading, error, data } = useQuery<
    CustomLabelsV2ByCourseBranchQuery,
    CustomLabelsV2ByCourseBranchQueryVariables
  >(CustomLabelsQuery, {
    variables,
    skip: !courseBranchId || loadingProductFeatures || !enableCustomLabels,
  });

  const customLabels = data?.CustomLabelsV2?.byCourseBranch?.elements?.[0];
  const { useModuleNameInsteadOfWeek } = customLabels || {};
  // When custom labels are disabled we skip WeekToModuleStatus; isModuleDefaultWhenSkipped: true
  // ensures replaceCustomContent still yields default "Module N" (e.g. in syllabus) without the query.
  const { replaceCustomContent } = useCustomLabelsMapping(courseBranchId || '', customLabels, {
    skipWeekToModuleQuery: loadingProductFeatures || !enableCustomLabels,
    isModuleDefaultWhenSkipped: true,
  });

  return {
    loading: loadingProductFeatures || loading,
    error,
    customLabels,
    replaceCustomContent,
    useModuleNameInsteadOfWeek,
  };
};

/**
 * Lightweight prefetch hook for SSR data providers.
 *
 * Only runs the ProductFeatures query to warm the Apollo cache. When the main
 * useCustomLabelsByCourseBranch hook runs on the client, it will use the cached
 * ProductFeatures data and conditionally run CustomLabelsV2/WeekToModuleStatus queries.
 *
 * @example
 * // In ConsumerPrefetchedCourseDataProvider.server.tsx
 * usePrefetchCustomLabelsByCourseBranch(course?.id);
 */
export const usePrefetchCustomLabelsByCourseBranch = (courseBranchId: string | undefined): void => {
  useCustomLabelsFromProductFeatures(courseBranchId);
};

export default useCustomLabelsByCourseBranch;
